import { Fragment, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  BasicTable,
  Button,
  ContentBox,
  ContentPage,
  DropdownAction,
  NoResults,
  Notice,
  Skeleton,
  Text,
  Tooltip,
} from "@ficatranquilo/contabilidade.com-ui";
import useService from "@hooks/useService";
import { DomainStatus, escapeHtml, formatDateTime, usePageHeader } from "@components/ow";
import LinkStatus from "@components/LinkStatus";
import { DetailTabs } from "../../Common/ContractDetail";
import AddMembersModal from "./AddMembersModal";
import GroupNameModal from "./GroupNameModal";
import RemoveMemberModal from "./RemoveMemberModal";
import { candidateOptions, groupViewers } from "./viewers";

const LIST_PATH = "/c/confidential-groups";

const TAB_MEMBERS = "pessoas";
const TAB_VIEWERS = "quem-pode-ver";

const LEFT_HINT = "Saiu da empresa. Continua no grupo para que o histórico dela siga confidencial.";

const NO_READS = 'Ainda não vê nada: falta "Ver contratos", "Ver cronogramas", "Ver NFs", "Ver ponto" ou "Ver horas".';

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Reads = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSizes.medium};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  line-height: ${({ theme }) => theme.fontSizes.large_x};
  color: ${({ theme }) => theme.colors.N900};
`;

const Help = styled.span`
  cursor: help;
  text-decoration: underline dotted;
`;

function Person({ user }) {
  return (
    <div>
      <Text $text={escapeHtml(user.name || "-")} $variant="paragraph_small_bold" $color="N900" />
      <Text $text={escapeHtml(user.email || "")} $variant="caption_medium" $color="N400" />
    </div>
  );
}

/** Situação da pessoa na empresa: desligada (sem vínculo ativo), ADM ou a do vínculo. */
function MemberStatus({ member }) {
  if (member.link_status === null) {
    return (
      <Tooltip content={LEFT_HINT} $position="top" $size="medium" $color="dark">
        <span>
          <DomainStatus domain="prestador" value="desligada" />
        </span>
      </Tooltip>
    );
  }
  if (member.role === "ADM") return <DomainStatus domain="prestador" value="administrador" />;
  return <LinkStatus status={member.link_status} />;
}

/** O que a pessoa vê, em texto, como o resumo de Permissões. */
function ViewerReads({ reads }) {
  if (reads.length === 0) return <Text $text={NO_READS} $variant="caption_medium" $color="N400" />;
  return (
    <Reads>
      {reads.map((read, index) => (
        <Fragment key={read.key}>
          {index > 0 ? " · " : null}
          {read.title ? (
            <Tooltip content={read.title} $position="top" $size="medium" $color="dark">
              <Help>{read.label}</Help>
            </Tooltip>
          ) : (
            read.label
          )}
        </Fragment>
      ))}
    </Reads>
  );
}

/**
 * Detalhe de um grupo confidencial (modelo Detalhe do DS OneWork). Só ADM (guard da
 * rota). Só entram colaboradores com vínculo ativo; quem sai da empresa continua no
 * grupo. "Quem pode ver" se edita em Permissões: aqui só se lê.
 */
export default function ConfidentialGroupDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const groupsService = useService("confidentialGroups");
  const userPermissions = useService("userPermissions");

  const [loading, setLoading] = useState(true);
  const [group, setGroup] = useState(null);
  // Colaboradores da empresa com as permissões (GET /user-permissions/users)
  const [people, setPeople] = useState([]);
  // "rename" | "add" | { remove: member }
  const [modal, setModal] = useState(null);

  const name = group?.name || "Grupo confidencial";
  // O Layout escapa o título; o breadcrumb é texto
  usePageHeader({
    title: name,
    breadcrumb: [
      { page: "Grupos confidenciais", route: LIST_PATH },
      { page: name, route: `${LIST_PATH}/${id}` },
    ],
  });

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function load() {
    setLoading(true);
    try {
      const [groupResp, usersResp] = await Promise.all([groupsService.getGroup(id), userPermissions.listUsers()]);
      setGroup(groupResp?.data);
      setPeople(usersResp?.data || []);
      setLoading(false);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.msg || "Erro ao carregar o grupo.");
      navigate(LIST_PATH);
    }
  }

  const members = useMemo(() => group?.members || [], [group]);
  const candidates = useMemo(() => candidateOptions(people, members), [people, members]);
  const viewers = useMemo(() => groupViewers(people, Number(id)), [people, id]);
  const permissionsSearch = `?grupo=confidential_data&confidencial=${id}`;

  const tab = searchParams.get("tab") === TAB_VIEWERS ? TAB_VIEWERS : TAB_MEMBERS;
  const closeModal = () => setModal(null);
  const openAdd = () => setModal("add");
  const openPermissions = () => navigate(`/c/permissions${permissionsSearch}`);

  // Lança em erro: o GroupNameModal fica aberto
  async function rename(newName) {
    try {
      const result = await groupsService.renameGroup(id, newName);
      setGroup((g) => ({ ...g, name: result?.data?.name ?? newName }));
      toast.success("Grupo renomeado.");
      setModal(null);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.msg || "Erro ao salvar o grupo.");
      throw err;
    }
  }

  function afterAdd(updated) {
    if (updated) setGroup(updated);
    setModal(null);
  }

  function afterRemove(userId) {
    setGroup((g) => ({ ...g, members: g.members.filter((m) => m.user.id !== userId) }));
    setModal(null);
  }

  const memberColumns = [
    { id: "person", header: "Colaborador", size: 460, cell: ({ row }) => <Person user={row.original.user} /> },
    { id: "status", header: "Situação", size: 240, cell: ({ row }) => <MemberStatus member={row.original} /> },
    {
      id: "added_at",
      header: "Incluída em",
      size: 200,
      cell: ({ row }) => (row.original.added_at ? formatDateTime(row.original.added_at) : "—"),
    },
    {
      id: "actions",
      // O "Ações" da lib tem 70px; abaixo disso a seta sai da célula
      size: 80,
      minSize: 80,
      maxSize: 80,
      align: "right",
      cell: ({ row }) => (
        <DropdownAction
          options={[{ label: "Tirar do grupo", action: () => setModal({ remove: row.original }), isImportant: true }]}
        />
      ),
    },
  ];

  const viewerColumns = [
    { id: "person", header: "Colaborador", size: 360, cell: ({ row }) => <Person user={row.original.user} /> },
    { id: "status", header: "Situação", size: 220, cell: ({ row }) => <LinkStatus status={row.original.link_status} /> },
    { id: "reads", header: "O que vê", size: 480, cell: ({ row }) => <ViewerReads reads={row.original.reads} /> },
  ];

  if (loading) {
    return (
      <ContentPage>
        <ContentBox>
          <Stack>
            <Skeleton $height={44} />
            <Skeleton $height={300} />
          </Stack>
        </ContentBox>
      </ContentPage>
    );
  }

  const tabs = [
    {
      value: TAB_MEMBERS,
      label: `Pessoas (${members.length})`,
      content: (
        <Stack>
          <Notice
            $variant="gray"
            $text="Contratos, cronogramas, valores, notas fiscais, o ponto e as horas destas pessoas só aparecem para os administradores e para quem tiver permissão no grupo."
          />
          <BasicTable
            data={members}
            columns={memberColumns}
            getRowId={(row) => String(row.user.id)}
            noResults={
              <NoResults notResultText="Nenhuma pessoa no grupo" includeText="Incluir pessoas" includeAction={openAdd} />
            }
          />
        </Stack>
      ),
    },
    {
      value: TAB_VIEWERS,
      label: `Quem pode ver (${viewers.length})`,
      content: (
        <Stack>
          <Notice
            $variant="gray"
            $text='Além dos administradores, veem os dados destas pessoas os colaboradores com "Ver dados confidenciais" neste grupo, dentro do que as outras permissões deles já mostram.'
          />
          <BasicTable
            data={viewers}
            columns={viewerColumns}
            getRowId={(row) => String(row.user.id)}
            onRowClick={(viewer) =>
              navigate(`/c/permissions/${viewer.user.id}`, { state: { listSearch: permissionsSearch } })
            }
            noResults={
              <NoResults
                notResultText="Só os administradores veem os dados deste grupo"
                includeText="Gerenciar em Permissões"
                includeAction={openPermissions}
              />
            }
          />
        </Stack>
      ),
    },
  ];

  const actions = (
    <>
      <Button $text="Renomear" $variant="outline" $action={() => setModal("rename")} $noWidth />
      {tab === TAB_MEMBERS ? (
        <Button $text="Incluir pessoas" $variant="primary" $action={openAdd} $noWidth />
      ) : (
        <Button $text="Gerenciar em Permissões" $variant="outline" $action={openPermissions} $noWidth />
      )}
    </>
  );

  return (
    <ContentPage>
      <ContentBox>
        <DetailTabs
          tabs={tabs}
          value={tab}
          onChange={(value) => setSearchParams(value === TAB_VIEWERS ? { tab: value } : {}, { replace: true })}
          actions={actions}
        />
      </ContentBox>

      {modal === "rename" ? <GroupNameModal group={group} onClose={closeModal} onSave={rename} /> : null}
      {modal === "add" ? (
        <AddMembersModal groupId={id} options={candidates} onClose={closeModal} onAdded={afterAdd} />
      ) : null}
      {modal?.remove ? (
        <RemoveMemberModal groupId={id} member={modal.remove} onClose={closeModal} onRemoved={afterRemove} />
      ) : null}
    </ContentPage>
  );
}
